import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  ShowChart,
  Refresh,
  Timeline,
  AccessTime,
  Warning
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  fetchUserRecentTradeSignals,
  selectUserRecentTradeSignals,
  selectUserTradeSignalsLoading,
  selectUserTradeSignalsError
} from '../features/userTradeSignals/userTradeSignalsSlice';
import { getTradeSignalAnalysis } from '../api/tradeSignals';
import { strategySymbolApi } from '../api/strategySymbols';
import { visualStrategiesApi } from '../api/visualStrategies';
import DWLFLoader from './DWLFLoader';
import useIsDark from '../hooks/useIsDark';
import { stopInForce, trailingStopLevel, trailingExit } from '../utils/trailingStop';
import { signalRowTrend } from '../utils/signalRowTrend';

const RecentTradeSignals = ({ title = 'Recent Signals' }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = useIsDark();
  
  const recentSignals = useSelector(selectUserRecentTradeSignals);
  const loading = useSelector(selectUserTradeSignalsLoading);
  const error = useSelector(selectUserTradeSignalsError);
  
  // Track if user has any custom strategies configured
  const [hasUserStrategies, setHasUserStrategies] = useState(null); // null = loading, true/false = loaded
  
  // Track if user has uncompiled strategies
  const [hasUncompiledStrategies, setHasUncompiledStrategies] = useState(false);

  const [systemSignals, setSystemSignals] = useState([]);
  const [systemLoading, setSystemLoading] = useState(false);
  const [systemError, setSystemError] = useState(null);

  const fetchSystemSignals = useCallback(async () => {
    setSystemLoading(true);
    setSystemError(null);

    try {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 5);

      const data = await getTradeSignalAnalysis({
        fromDate: fromDate.toISOString().split('T')[0],
        limit: 10,
        sortBy: 'date',
        sortOrder: 'desc'
      });

      setSystemSignals(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to fetch system trade signals';
      setSystemError(message);
      setSystemSignals([]);
    } finally {
      setSystemLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchUserRecentTradeSignals({ limit: 10 }));
    fetchSystemSignals();
    
    // Check if user has any custom strategies configured and if any need compilation
    const checkUserStrategies = async () => {
      try {
        const response = await strategySymbolApi.getUserAssociations(true);
        // API returns { associations: [...] }
        const associations = response?.associations || response;
        const hasStrategies = Array.isArray(associations) && associations.length > 0;
        setHasUserStrategies(hasStrategies);
        
        // If user has strategies, check if any need compilation
        if (hasStrategies) {
          try {
            const strategiesResponse = await visualStrategiesApi.listStrategies({ limit: 100 });
            const strategies = strategiesResponse?.strategies || strategiesResponse || [];
            const uncompiled = strategies.some((strategy) => {
              const compiledAt = strategy?.metadata?.compiledAt;
              const updatedAt = strategy?.updatedAt;
              // Needs compile if never compiled OR if updated after compile
              return !compiledAt || (updatedAt && new Date(updatedAt) > new Date(compiledAt));
            });
            setHasUncompiledStrategies(uncompiled);
          } catch (strategiesErr) {
            // Silently fail - don't block the main functionality
            setHasUncompiledStrategies(false);
          }
        } else {
          setHasUncompiledStrategies(false);
        }
      } catch (err) {
        // On error, assume they might have strategies (don't show CTA on error)
        setHasUserStrategies(true);
        setHasUncompiledStrategies(false);
      }
    };
    checkUserStrategies();
  }, [dispatch, fetchSystemSignals]);

  const handleRefresh = () => {
    dispatch(fetchUserRecentTradeSignals({ limit: 10 }));
    fetchSystemSignals();
  };

  const isSignalActive = (signal) => {
    if (!signal) {
      return false;
    }
    if (signal.active === 'true' || signal.active === true) {
      return true;
    }
    if (typeof signal.status === 'string') {
      const normalized = signal.status.toLowerCase();
      return normalized === 'open' || normalized === 'active';
    }
    return false;
  };

  const hasSignalHitTarget = (signal) => {
    if (!signal) {
      return false;
    }
    const normalizedStatus = typeof signal.status === 'string' ? signal.status.toLowerCase() : null;
    return Boolean(
      signal.target10RHitAt ||
      signal.target5RHitAt ||
      signal.target3RHitAt ||
      (normalizedStatus && ['closed_win', 'winner', 'target_hit', 'completed_win'].includes(normalizedStatus))
    );
  };

  const isSignalStopped = (signal) => {
    if (!signal) {
      return false;
    }
    const numericRR = Number(signal.currentRR);
    const normalizedStatus = typeof signal.status === 'string' ? signal.status.toLowerCase() : null;
    return Boolean(
      signal.stoppedOutAt ||
      numericRR === -1 ||
      (normalizedStatus && ['stopped', 'closed_loss', 'stopped_out', 'cancelled'].includes(normalizedStatus))
    );
  };

  const getStatusChip = (signal) => {
    if (isSignalActive(signal)) {
      return (
        <Chip
          size="small"
          label="Active"
          color="success"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      );
    }

    if (hasSignalHitTarget(signal)) {
      return (
        <Chip
          size="small"
          label="Winner"
          color="success"
          sx={{ fontSize: '0.75rem' }}
        />
      );
    }

    // A trailing stop-out (DWLF-341) is often a profit: say which, before 'Stopped'.
    const trailed = trailingExit(signal);
    if (trailed) {
      return (
        <Chip
          size="small"
          label="Trailing stop"
          color={trailed.chipColor}
          sx={{ fontSize: '0.75rem' }}
        />
      );
    }

    if (isSignalStopped(signal)) {
      return (
        <Chip
          size="small"
          label="Stopped"
          color="error"
          sx={{ fontSize: '0.75rem' }}
        />
      );
    }

    return (
      <Chip
        size="small"
        label="Inactive"
        color="default"
        sx={{ fontSize: '0.75rem' }}
      />
    );
  };

  const getSourceChip = (signal) => {
    if (signal?.source === 'system') {
      return (
        <Chip
          size="small"
          label="System"
          color="info"
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
      );
    }

    if (signal?.source === 'user') {
      return (
        <Chip
          size="small"
          label="User"
          color="secondary"
          sx={{ fontSize: '0.7rem' }}
        />
      );
    }

    return null;
  };

  const formatPrice = (price) => {
    if (price === undefined || price === null || price === '') {
      return 'N/A';
    }
    const numeric = Number(price);
    if (!Number.isFinite(numeric)) {
      return 'N/A';
    }
    return `$${numeric.toFixed(2)}`;
  };

  const formatRR = (signal) => {
    const rrValue = signal?.currentRR ?? signal?.riskRewardRatio ?? signal?.riskReward;
    if (rrValue === undefined || rrValue === null || rrValue === '') {
      return 'N/A';
    }
    const numeric = Number(rrValue);
    if (!Number.isFinite(numeric)) {
      return 'N/A';
    }
    return `${numeric.toFixed(2)}R`;
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }
    const showYear = date.getFullYear() !== new Date().getFullYear();
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: showYear ? 'numeric' : undefined
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) {
      return '';
    }
    const hasTimeComponent = dateString.includes('T') || dateString.includes(':');
    if (!hasTimeComponent) {
      return '';
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getSignalDateValue = (signal) =>
    signal?.date || signal?.createdAt || signal?.updatedAt || signal?.timestamp || null;

  const getSignalTimeValue = (signal) =>
    signal?.createdAt ||
    signal?.updatedAt ||
    (signal?.date && (signal.date.includes('T') || signal.date.includes(':')) ? signal.date : null);

  const getStrategyText = (signal) =>
    signal?.strategyDescription ||
    signal?.strategyDetails?.summary ||
    signal?.reasonSummary ||
    signal?.strategyName ||
    signal?.strategy ||
    signal?.strategy_type ||
    null;

  const getSignalTimestamp = useCallback((signal) => {
    if (!signal) {
      return 0;
    }
    const candidates = [signal.createdAt, signal.updatedAt, signal.date, signal.timestamp];
    for (const value of candidates) {
      if (!value) {
        continue;
      }
      const time = new Date(value).getTime();
      if (!Number.isNaN(time)) {
        return time;
      }
    }
    return 0;
  }, []);

  const combinedSignals = useMemo(() => {
    const userSignals = Array.isArray(recentSignals)
      ? recentSignals.map((signal) => ({ ...signal, source: 'user' }))
      : [];
    const systemSignalsWithSource = Array.isArray(systemSignals)
      ? systemSignals.map((signal) => ({ ...signal, source: 'system' }))
      : [];

    const merged = [...userSignals, ...systemSignalsWithSource];
    merged.sort((a, b) => getSignalTimestamp(b) - getSignalTimestamp(a));
    return merged.slice(0, 10);
  }, [recentSignals, systemSignals, getSignalTimestamp]);

  const recentSignalsError = error?.recentSignals;
  const errorMessages = useMemo(() => {
    const messages = [];
    if (recentSignalsError) {
      messages.push(recentSignalsError);
    }
    if (systemError) {
      messages.push(systemError);
    }
    return messages;
  }, [recentSignalsError, systemError]);

  const isLoading = loading.recentSignals || systemLoading;
  const fatalError = errorMessages.length > 0 && combinedSignals.length === 0;

  if (isLoading) {
    return (
      <Card sx={{ width: '100%', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <DWLFLoader size="medium" />
      </Card>
    );
  }

  if (fatalError) {
    return (
      <Card sx={{ width: '100%', height: '400px' }}>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessages.map((message, index) => (
              <Typography key={index} variant="body2">
                {message}
              </Typography>
            ))}
          </Alert>
          <Box display="flex" justifyContent="center">
            <IconButton onClick={handleRefresh} color="primary">
              <Refresh />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ width: '100%' }}
    >
      <Card sx={{ width: '100%', height: '400px', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Timeline color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                (Last 5 days)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Refresh">
                <IconButton 
                  size="small" 
                  onClick={handleRefresh}
                  disabled={isLoading}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="View all signals">
                <IconButton 
                  size="small" 
                  component={Link} 
                  to="/my-signals"
                  sx={{ color: (theme.vars || theme).palette.primary.main }}
                >
                  <ShowChart fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Strategy CTA banner - show when user has no custom strategies configured */}
          {hasUserStrategies === false && (
            <Box 
              sx={{ 
                mb: 2, 
                p: 1.5, 
                borderRadius: 1, 
                backgroundColor: isDark 
                  ? 'rgba(144, 202, 249, 0.08)' 
                  : 'rgba(25, 118, 210, 0.08)',
                border: `1px solid ${isDark 
                  ? 'rgba(144, 202, 249, 0.2)' 
                  : 'rgba(25, 118, 210, 0.2)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Create your own strategy to get personalized signals
              </Typography>
              <Box
                component={Link}
                to="/strategies"
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: 1,
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }}
              >
                Create Strategy
              </Box>
            </Box>
          )}

          {/* Uncompiled strategies banner - show when user has strategies that need compilation */}
          {hasUserStrategies && hasUncompiledStrategies && (
            <Box
              component={Link}
              to="/strategies"
              sx={{
                mb: 2,
                p: 1,
                borderRadius: 1,
                backgroundColor: isDark
                  ? 'rgba(255, 167, 38, 0.1)'
                  : 'rgba(237, 108, 2, 0.1)',
                border: `1px solid ${(theme.vars || theme).palette.warning.main}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: isDark
                    ? 'rgba(255, 167, 38, 0.15)'
                    : 'rgba(237, 108, 2, 0.15)',
                }
              }}
            >
              <Warning sx={{ color: 'warning.main', fontSize: 18 }} />
              <Typography variant="caption" color="warning.main" fontWeight={500}>
                Some strategies need to be compiled
              </Typography>
            </Box>
          )}

          {errorMessages.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {errorMessages.map((message, index) => (
                <Typography key={`partial-error-${index}`} variant="caption" display="block">
                  {message}
                </Typography>
              ))}
            </Alert>
          )}

          {/* Content */}
          {combinedSignals.length === 0 ? (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              flexGrow={1}
              gap={2}
            >
              <Timeline sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No recent signals found
              </Typography>
              <Typography variant="caption" color="text.disabled" textAlign="center">
                System signals and visual strategy signals will appear here
              </Typography>
            </Box>
          ) : (
            <Box flexGrow={1} overflow="hidden">
              <List sx={{ p: 0, maxHeight: '300px', overflow: 'auto' }}>
                {combinedSignals.map((signal, index) => {
                  const timestamp = getSignalTimestamp(signal);
                  const key = signal.signalId || signal.contextHash || `${signal.source || 'unknown'}-${signal.symbol || 'symbol'}-${timestamp}-${index}`;
                  const strategyText = getStrategyText(signal);
                  const dateValue = getSignalDateValue(signal);
                  const timeValue = getSignalTimeValue(signal);
                  const formattedTime = formatTime(timeValue);
                  const symbolLabel = signal.symbol || 'Unknown';
                  const formattedSymbol = typeof signal.symbol === 'string'
                    ? signal.symbol.replace(/\//g, '-')
                    : '';
                  const targetPath = formattedSymbol ? `/markets/${formattedSymbol}` : '/markets';

                  return (
                    <React.Fragment key={key}>
                      <ListItem 
                        component={Link}
                        to={targetPath}
                        sx={{ 
                          px: 0, 
                          py: 1,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: (theme.vars || theme).palette.action.hover,
                            borderRadius: 1
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {{
                            up: <TrendingUp color="success" fontSize="small" />,
                            flat: <TrendingFlat color="action" fontSize="small" />,
                            down: <TrendingDown color="error" fontSize="small" />,
                            faded: <TrendingDown color="warning" fontSize="small" />,
                          }[signalRowTrend(signal, {
                            active: isSignalActive(signal),
                            hitTarget: hasSignalHitTarget(signal),
                            stopped: isSignalStopped(signal),
                          })]}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                              <Typography variant="body2" fontWeight={600} color="text.primary">
                                {symbolLabel}
                              </Typography>
                              {getStatusChip(signal)}
                              {getSourceChip(signal)}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Box display="flex" alignItems="center" gap={2} mb={0.5} flexWrap="wrap">
                                <Typography variant="caption" color="text.secondary">
                                  Entry: {formatPrice(signal.initialPrice ?? signal.entryPrice ?? signal.entry)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {/* A trailing stop's level now (DWLF-341); otherwise the fixed stop. */}
                                  {trailingStopLevel(signal) !== null ? 'Trailing stop' : 'Stop'}: {formatPrice(stopInForce(signal))}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  R/R: {formatRR(signal)}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <AccessTime sx={{ fontSize: 12, color: 'text.disabled' }} />
                                <Typography variant="caption" color="text.disabled">
                                  {formatDate(dateValue)}
                                  {formattedTime ? ` ${formattedTime}` : ''}
                                </Typography>
                                {strategyText && (
                                  <Typography variant="caption" color="text.disabled">
                                    • {strategyText}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < combinedSignals.length - 1 && (
                        <Divider variant="inset" component="li" sx={{ ml: 5 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default RecentTradeSignals;
